import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress
} from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { createPetition, updatePetition, getPetitionById } from '../lib/supabase';
import toast from 'react-hot-toast';

const categories = [
  'Mediu și Climă',
  'Drepturile Muncii',
  'Sănătate Publică',
  'Educație',
  'Justiție Socială',
  'Politici Europene',
  'Economie și Finanțe',
  'Migrație și Azil',
  'Tehnologie și Digitalizare',
  'Cultură și Arte',
  'Altele'
];

const CreatePetition = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      title: '',
      description: '',
      content: '',
      category: '',
      target_signatures: 1000,
      is_active: true
    }
  });

  useEffect(() => {
    if (isEdit) {
      fetchPetition();
    }
  }, [id, isEdit]);

  const fetchPetition = async () => {
    try {
      const petition = await getPetitionById(id);
      reset({
        title: petition.title,
        description: petition.description,
        content: petition.content,
        category: petition.category || '',
        target_signatures: petition.target_signatures || 1000,
        is_active: petition.is_active
      });
    } catch (error) {
      toast.error('Eroare la încărcarea petiției');
      navigate('/admin');
    } finally {
      setInitialLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (isEdit) {
        await updatePetition(id, data);
        toast.success('Petiția a fost actualizată cu succes');
      } else {
        await createPetition(data);
        toast.success('Petiția a fost creată cu succes');
      }
      navigate('/admin');
    } catch (error) {
      toast.error(isEdit ? 'Eroare la actualizarea petiției' : 'Eroare la crearea petiției');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin')}
          sx={{ mb: 2 }}
        >
          Înapoi la Dashboard
        </Button>
        <Typography variant="h3" component="h1" gutterBottom>
          {isEdit ? 'Editează Petiția' : 'Creează Petiție Nouă'}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {isEdit ? 'Modifică detaliile petiției existente' : 'Completează formularul pentru a crea o petiție nouă'}
        </Typography>
      </Box>

      <Paper elevation={4} sx={{ p: 4, borderRadius: 3 }}>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            {/* Titlu */}
            <Grid item xs={12}>
              <Controller
                name="title"
                control={control}
                rules={{ 
                  required: 'Titlul este obligatoriu',
                  minLength: { value: 10, message: 'Titlul trebuie să aibă cel puțin 10 caractere' },
                  maxLength: { value: 200, message: 'Titlul nu poate depăși 200 de caractere' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Titlul Petiției"
                    placeholder="Introduceți un titlu clar și concis..."
                    error={!!errors.title}
                    helperText={errors.title?.message}
                    disabled={loading}
                  />
                )}
              />
            </Grid>

            {/* Descriere scurtă */}
            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                rules={{ 
                  required: 'Descrierea este obligatorie',
                  minLength: { value: 50, message: 'Descrierea trebuie să aibă cel puțin 50 de caractere' },
                  maxLength: { value: 500, message: 'Descrierea nu poate depăși 500 de caractere' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={3}
                    label="Descriere Scurtă"
                    placeholder="O descriere scurtă care va apărea pe lista de petiții..."
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    disabled={loading}
                  />
                )}
              />
            </Grid>

            {/* Conținut complet */}
            <Grid item xs={12}>
              <Controller
                name="content"
                control={control}
                rules={{ 
                  required: 'Conținutul este obligatoriu',
                  minLength: { value: 100, message: 'Conținutul trebuie să aibă cel puțin 100 de caractere' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={8}
                    label="Conținutul Complet al Petiției"
                    placeholder="Descrierea detaliată a petiției, motivele pentru care este importantă, ce se dorește să se realizeze..."
                    error={!!errors.content}
                    helperText={errors.content?.message}
                    disabled={loading}
                  />
                )}
              />
            </Grid>

            {/* Categorie și Obiectiv */}
            <Grid item xs={12} md={6}>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Categoria</InputLabel>
                    <Select
                      {...field}
                      label="Categoria"
                      disabled={loading}
                    >
                      <MenuItem value="">
                        <em>Selectează o categorie</em>
                      </MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="target_signatures"
                control={control}
                rules={{ 
                  required: 'Obiectivul de semnături este obligatoriu',
                  min: { value: 100, message: 'Obiectivul trebuie să fie cel puțin 100' },
                  max: { value: 1000000, message: 'Obiectivul nu poate depăși 1,000,000' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Obiectiv Semnături"
                    placeholder="1000"
                    error={!!errors.target_signatures}
                    helperText={errors.target_signatures?.message}
                    disabled={loading}
                    onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                  />
                )}
              />
            </Grid>

            {/* Status activ */}
            <Grid item xs={12}>
              <Controller
                name="is_active"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        checked={field.value}
                        disabled={loading}
                      />
                    }
                    label="Petiție activă (va fi vizibilă publicului)"
                  />
                )}
              />
            </Grid>

            {/* Info Box */}
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Sfaturi pentru o petiție de succes:</strong>
                  <br />
                  • Folosește un titlu clar și atractiv
                  <br />
                  • Explică problema și soluția propusă
                  <br />
                  • Include date și argumente concrete
                  <br />
                  • Menționează beneficiile pentru cetățeni
                  <br />
                  • Setează un obiectiv realist de semnături
                </Typography>
              </Alert>
            </Grid>

            {/* Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin')}
                  disabled={loading}
                >
                  Anulează
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                  disabled={loading}
                  size="large"
                >
                  {loading 
                    ? (isEdit ? 'Se actualizează...' : 'Se creează...') 
                    : (isEdit ? 'Actualizează Petiția' : 'Creează Petiția')
                  }
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default CreatePetition;
